import { useEffect, useState, useRef } from 'react'
import imageData from './images.json'

const DELAY = 1000

interface MicroficheProps {
  showImagesWithDelay?: boolean
}

const useMicrofiche = (props?: MicroficheProps) => {
  // For loading images
  const { showImagesWithDelay } = props || {}
  const [images, setImages] = useState<string[]>([])
  const [loading, setLoading] = useState<boolean>(true)

  // For zoom
  const [isZoomed, setIsZoomed] = useState(true) // Start zoomed in
  const [zoomOrigin, setZoomOrigin] = useState({ x: 0, y: 0 }) // Zoom origin set to top-left
  const [delayPassed, setDelayPassed] = useState(false) // Delay to show overlays

  const indexRef = useRef(0) // Ref to track the current index without triggering re-renders
  const imagesRef = useRef<string[]>([]) // Ref to track the loaded images without triggering re-renders

  // =======================
  // UseEffects
  // =======================

  // Delay overlay appearance until zoom animation completes
  useEffect(() => {
    if (isZoomed) {
      document.body.style.overflow = 'hidden' // Disable scroll on body when zoomed
      setTimeout(() => {
        setDelayPassed(true) // Set delay after zooming in completes
      }, 300) // Wait for 300ms before showing overlays
    } else {
      document.body.style.overflow = 'auto' // Enable scroll when zoom is off
      setDelayPassed(false) // Hide overlays immediately when zoomed out
    }
  }, [isZoomed])

  // Load images
  useEffect(() => {
    const imageNames = imageData.images // List of image filenames

    // Show each image one at a time sequentially. This is broken. They currently all show at once when timer completes.
    const addImageWithDelay = () => {
      if (indexRef.current < imageNames.length) {
        const imageName = imageNames[indexRef.current]
        const imagePath = `/images/${imageName}`

        // Check if the image is already in the state to avoid duplicates
        if (imagesRef.current.includes(imagePath)) {
          indexRef.current += 1
          addImageWithDelay()
          return
        }

        // Add the image to the state and imagesRef
        setImages((prevImages) => {
          const updatedImages = [...prevImages, imagePath]
          imagesRef.current = updatedImages
          return updatedImages
        })

        indexRef.current += 1

        // Load the next image after the delay
        setTimeout(addImageWithDelay, DELAY)
      } else {
        setLoading(false) // Once all images are loaded, stop loading
      }
    }

    // If `showImagesWithDelay` is true, load images with delay
    if (showImagesWithDelay) {
      addImageWithDelay()
    } else {
      // If `showImagesWithDelay` is false, load all images at once
      setImages(imageNames.map((imageName) => `/images/${imageName}`))
      setLoading(false) // No delay, immediately set loading to false
    }
  }, [showImagesWithDelay])

  // =======================
  // Zoom
  // =======================
  const handleContainerClick = (e: React.MouseEvent) => {
    const { clientX, clientY } = e
    const { left, top, width, height } = e.currentTarget.getBoundingClientRect()

    // Calculate the click position relative to the container
    const x = ((clientX - left) / width) * 100
    const y = ((clientY - top) / height) * 100

    setZoomOrigin({ x, y }) // Set zoom origin dynamically
    setIsZoomed(!isZoomed) // Toggle zoom on click
  }

  return {
    images,
    loading,
    handleContainerClick,
    zoom: { isZoomed, zoomOrigin, delayPassed },
  }
}

export default useMicrofiche
